import { connect } from 'react-redux';
import { useCallback, useContext, useEffect, useRef } from 'react';
import { Form, FormRenderProps } from 'react-final-form';
import { FormApi } from 'final-form';
import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays';
import arrayMutators from 'final-form-arrays';

import { Modal } from 'UIPackage/ModalModule/components/Modal';
import { ModalHeader } from 'UIPackage/ModalModule/components/ModalHeader';
import { ModalBody } from 'UIPackage/ModalModule/components/ModalBody';
import { ModalActions } from 'UIPackage/ModalModule/components/ModalActions';
import { Button } from 'UIPackage/MainModule/components/Button';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import {
  getKnmErknmPositionsSelectOptions,
  getKnmFormRef,
  getKnmOnSaveButtonClick,
} from 'KnmPackage/MainModule/selectors/knmSelectors';
import { IDictionaryPositionSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryPositionSelectOption';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { FieldMark } from 'KnmPackage/MainModule/components/FieldMark/FieldMark';
import { getCatalogTerminationReasonSelectOptions } from 'CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { ITerminationReasonSelectOption } from 'CorePackage/CatalogsModule/interfaces/ITerminationReasonSelectOption';
import { Row } from 'UIPackage/FormModule/components/Row';
import { CloseButton } from 'UIPackage/MainModule/components/CloseButton';
import { IErknmFormAgreementTerminate } from 'KnmErknmPackage/MainModule/interfaces/IErknmFormAgreementTerminate';
import { getAgreementTerminateValidate } from 'KnmErknmPackage/MainModule/common/getAgreementTerminateValidate';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { makeRequired } from 'CorePackage/MainModule/validators/isRequired';
import { REQUIRED_ERROR } from 'CorePackage/MainModule/constants/validationErrors';
import { getUserRoleIsSupervisor } from 'AuthPackage/MainModule/selectors/authSelectors';
import { IUserOrganisationSelectOption } from 'CorePackage/CatalogsModule/interfaces/IUserOrganisationSelectOption';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import {
  getDecisionFormRef,
  getDecisionOnSaveButtonClick,
  getDecisionPositionsSelectOptions,
} from 'DecisionPackage/MainModule/selectors/decisionSelectors';
import { AgreementsContext } from 'KnmErknmPackage/ErkmAgreementsModule/AgreementsContext';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';
import { IDecisionForm } from 'DecisionPackage/MainModule/interfaces/IDecisionForm';
import { IKnmForm } from 'KnmPackage/MainModule/interfaces/IKnmForm';

import styles from './TerminationAgreementModal.module.scss';
import { AttachmentsInModals } from '../../AttachmentsInModals/AttachmentsInModals';

interface ITerminationAgreementModalProps {
  name: string;
  visible: boolean;
  onClose: () => void;
  onCloseAll: () => void;
  prosecutorOrganization: IUserOrganisationSelectOption | undefined;
}

interface ITerminationAgreementModalStoreProps {
  positionsSelectOptions: IDictionaryPositionSelectOption[] | undefined;
  decisionPositionsSelectOptions: IDictionaryPositionSelectOption[] | undefined;
  terminationReasonsSelectOptions: ITerminationReasonSelectOption[];
  isSupervisor: boolean;
  formRefKnm: IKnmFormRef | undefined;
  decisionFormRef: IDecisionFormRef | undefined;
  onSaveButtonClickKnmAndPm: (() => void) | undefined;
  onSaveButtonClickDecision: (() => void) | undefined;
}

interface IForm extends IErknmFormAgreementTerminate {}

const TerminationAgreementModalWithoutStore = ({
  name,
  visible,
  onClose,
  onCloseAll,
  formRefKnm,
  isSupervisor,
  decisionFormRef,
  prosecutorOrganization,
  positionsSelectOptions,
  onSaveButtonClickDecision,
  onSaveButtonClickKnmAndPm,
  decisionPositionsSelectOptions,
  terminationReasonsSelectOptions,
}: ITerminationAgreementModalProps & ITerminationAgreementModalStoreProps) => {
  const formRef = useRef<FormApi<IForm, Partial<IForm>>>();
  const { isDecision } = useContext(AgreementsContext);

  useEffect(() => {
    if (prosecutorOrganization && visible) {
      formRef.current?.change('prosecutorOrganization' as keyof IForm, prosecutorOrganization);
      formRef.current?.change('whoMadeDecision' as keyof IForm, isSupervisor);
    }
  }, [formRef, prosecutorOrganization, visible]);

  useEffect(() => {
    if (visible) {
      // @ts-ignore
      formRef.current?.change('terminationReasons', [undefined]);
    }
  }, [visible]);

  const handleClose = () => {
    onClose();
    formRef?.current?.reset();
  };

  const handleSave = useCallback(
    (values: IForm) => {
      if (isDecision) {
        // @ts-ignore
        decisionFormRef?.current.change(`${name}.terminate` as keyof IDecisionForm, values);

        if (isSupervisor) {
          decisionFormRef?.current.change(`${name}.signed` as keyof IDecisionForm, false);
          onSaveButtonClickDecision && onSaveButtonClickDecision();
        }
      } else {
        // @ts-ignore
        formRefKnm?.current.change(`${name}.terminate` as keyof IKnmForm, values);

        if (isSupervisor) {
          formRefKnm?.current.change(`${name}.signed` as keyof IKnmForm, false);
          onSaveButtonClickKnmAndPm && onSaveButtonClickKnmAndPm();
        }
      }
      onCloseAll();
      formRef?.current?.reset();
    },
    [name, formRef, decisionFormRef, onSaveButtonClickDecision, onSaveButtonClickKnmAndPm, isSupervisor, onCloseAll],
  );

  const addTerminationReason = useCallback(() => {
    formRef.current?.mutators.push('terminationReasons', undefined);
  }, [formRef]);

  const renderContentForm = useCallback(
    ({ handleSubmit, form, values, errors, touched }: FormRenderProps<IForm>) => {
      formRef.current = form;

      const unusedTerminationReasonsSelectOptions = terminationReasonsSelectOptions.filter(
        reasons => !values.terminationReasons?.find(i => i?.id === reasons.id),
      );

      return (
        <>
          <ModalBody size="content" padding="small">
            <div className={styles.ContentWrapper}>
              <FieldBlock
                label={
                  <FieldMark
                    fieldName="terminationReasons"
                    label="Основания для признания соглашения неисполненным и расторжения соглашения"
                  />
                }
                button={
                  <Button
                    className="shared-btn-add"
                    text="Добавить"
                    type="button"
                    variant="link"
                    color="primary"
                    onClick={addTerminationReason}
                  />
                }
              >
                <div className={styles.ReasonsWrapper}>
                  <FieldArray name="terminationReasons" validate={makeRequired(REQUIRED_ERROR)}>
                    {({ fields }: FieldArrayRenderProps<ITerminationReasonSelectOption, HTMLElement>) => {
                      if (!fields.length) {
                        return null;
                      }

                      return fields.map((terminationReasonsName, index) => {
                        const fieldValue: ITerminationReasonSelectOption | undefined = fields.value?.[index];

                        return (
                          <Row key={`terminationReasons.${index}.${fieldValue?.id}`} margin="none" isListItem>
                            <div className={`shared-field-row ${styles.TerminationReasonsRow}`}>
                              <SelectInputField
                                name={terminationReasonsName}
                                placeholder={'Основание'}
                                size="medium"
                                options={unusedTerminationReasonsSelectOptions}
                              />
                              <CloseButton
                                className="shared-deleting-row-btn"
                                onClick={() => formRef.current?.mutators.remove('terminationReasons', index)}
                              />
                            </div>
                          </Row>
                        );
                      });
                    }}
                  </FieldArray>
                </div>
                {errors?.terminationReasons?.['FINAL_FORM/array-error'] && touched?.terminationReasons && (
                  <p className="shared-input-error">Поле не может быть пустым</p>
                )}
              </FieldBlock>
              <AttachmentsInModals
                formRef={formRef}
                formats=".pdf"
                name="decisionTerminationAttachment"
                title="Решение о признании соглашения неисполненным и расторжении соглашения"
                type="one"
                hideButtonAdd={!!values.decisionTerminationAttachment?.length}
              />
              <FieldBlock margin="top" label={'Решение принято'}>
                <FieldBlock
                  className={styles.FieldBlock}
                  label={
                    <FieldMark
                      fieldName="prosecutorOrganization"
                      label={isSupervisor ? 'Наименование органа контроля' : 'Наименования прокуратуры'}
                    />
                  }
                  placementLabel="left"
                  color="black"
                >
                  <TextInputField disabled name={`prosecutorOrganization.fullName`} />
                </FieldBlock>
              </FieldBlock>
              <FieldBlock
                label={<FieldMark fieldName="fioSigner" label={'ФИО принявшего решение'} />}
                color="black"
                className={styles.FieldBlock}
                placementLabel="left"
                margin="bottom"
              >
                <TextInputField name={`fioSigner`} placeholder={'ФИО'} />
              </FieldBlock>
              <FieldBlock
                label={<FieldMark fieldName="titleSigner" label={'Должность лица, принявшего решение'} />}
                color="black"
                className={styles.FieldBlock}
                placementLabel="left"
                margin="bottom"
              >
                {isSupervisor ? (
                  <SelectInputField
                    name={`titleSigner`}
                    placeholder="Выберите должность"
                    options={isDecision ? decisionPositionsSelectOptions : positionsSelectOptions}
                    isSearchable
                    size="medium"
                    opensOnTop
                  />
                ) : (
                  <TextInputField
                    name={`titleSigner.label`}
                    placeholder={'Должность'}
                    onChange={e =>
                      formRef.current?.change('titleSigner', {
                        label: e.target.value,
                        code: '',
                        guid: '',
                        id: '',
                        title: e.target.value,
                        value: {
                          // @ts-ignore
                          dictId: '',
                          dictVersionId: '',
                          title: e.target.value,
                        },
                      })
                    }
                  />
                )}
              </FieldBlock>
            </div>
          </ModalBody>
          <ModalActions>
            <Button text="Отменить" size="medium" type="button" onClick={handleClose} />
            <Button text="Сохранить" color="primary" size="medium" type="button" onClick={handleSubmit} />
          </ModalActions>
        </>
      );
    },
    [
      positionsSelectOptions,
      decisionPositionsSelectOptions,
      terminationReasonsSelectOptions,
      handleClose,
      isDecision,
      addTerminationReason,
      isSupervisor,
    ],
  );

  return (
    <Modal visible={visible} onClose={handleClose}>
      <ModalHeader type="small" title="Признание соглашения неисполненным" />
      <Form<IForm>
        render={renderContentForm}
        onSubmit={handleSave}
        validate={values => getAgreementTerminateValidate(values, isSupervisor)}
        mutators={{ ...arrayMutators }}
      />
    </Modal>
  );
};

const mapStateToProps = (state: IRootState): ITerminationAgreementModalStoreProps => ({
  positionsSelectOptions: getKnmErknmPositionsSelectOptions(state),
  terminationReasonsSelectOptions: getCatalogTerminationReasonSelectOptions(state),
  isSupervisor: getUserRoleIsSupervisor(state),
  decisionPositionsSelectOptions: getDecisionPositionsSelectOptions(state),
  formRefKnm: getKnmFormRef(state),
  decisionFormRef: getDecisionFormRef(state),
  onSaveButtonClickKnmAndPm: getKnmOnSaveButtonClick(state),
  onSaveButtonClickDecision: getDecisionOnSaveButtonClick(state),
});

export const TerminationAgreementModal = connect(mapStateToProps)(TerminationAgreementModalWithoutStore);
